import { useFormik, FormikHelpers } from "formik";
import * as Yup from "yup";

import {
  DisplaySettingsFields,
  FormikDisplaySettingsFields,
} from "../../../../../api/graphql/rooms/rooms";
import {
  MaxDuration,
  SensorEntitlement,
} from "../../../../../api/grpc/workplaces/workplaces";

const DisplaySchema = Yup.object().shape({
  main: Yup.object().shape({
    adminPin: Yup.string()
      .matches(/^[0-9]+$/, "Pin number must be only digits!")
      .min(4, "Pin number must be exactly 4 digits long!")
      .max(4, "Pin number must be exactly 4 digits long!"),
    backgroundImageUrl: Yup.string()
      .matches(/^https:\/\/(.*)$/, {
        message:
          "Please make sure that the link is from a secure source, starting with an https://",
        excludeEmptyString: true,
      })
      .matches(/^(.*).(png|jpg|jpeg)$/, {
        message:
          "Please make sure that image is from a supported format(.jpg, .jpeg or .png)",
        excludeEmptyString: true,
      }),
    logoImageUrl: Yup.string()
      .matches(/^https:\/\/(.*)$/, {
        message:
          "Please make sure that the link is from a secure source, starting with an https://",
        excludeEmptyString: true,
      })
      .matches(/^(.*).(png|jpg|jpeg)$/, {
        message:
          "Please make sure that image is from a supported format(.jpg, .jpeg or .png)",
        excludeEmptyString: true,
      }),
    layoutTheme: Yup.object().shape({
      backgroundImageOpacity: Yup.number()
        .min(0, "Opacity must be at least 0")
        .max(100, "Opacity must be at most 100"),
    }),
  }),
  display: Yup.object().shape({
    contentBoardURL: Yup.string().matches(/^https:\/\/(.*)$/, {
      message:
        "Please make sure that the link is from a secure source, starting with an https://",
      excludeEmptyString: true,
    }),
    screenLock: Yup.string()
      .matches(/^[0-9]+$/, "Screen lock number must be only digits!")
      .min(4, "Screen lock must be exactly 4 digits long!")
      .max(4, "Screen lock must be exactly 4 digits long!"),
  }),
});

interface UseDisplaySettingsFormOptions {
  enableReinitialize?: boolean;
  onSubmit: (
    values: FormikDisplaySettingsFields,
    formikHelpers: FormikHelpers<FormikDisplaySettingsFields>
  ) => Promise<any>;
}

export const DefaultThemeLayout = {
  useColorImage: false,
  backgroundImageOpacity: 100,
  themeVersion: "signature",
  customHexColors: {
    busy: "",
    free: "",
    tentative: "",
  },
};

// in room template sensor is sent as enum, in room details query it's sent as string, so we need to cover both cases
const toSensorEntitlement = (val: unknown): SensorEntitlement => {
  if (val == null) return SensorEntitlement.UNASSIGNED;
  if (typeof val === "number") return val as SensorEntitlement;
  return (
    SensorEntitlement[val as keyof typeof SensorEntitlement] ??
    SensorEntitlement.UNASSIGNED
  );
};

export type DisplaySettingsFormik = ReturnType<typeof useDisplaySettingsFormik>;

export const useDisplaySettingsFormik = (
  data: DisplaySettingsFields | null | undefined,
  opts: UseDisplaySettingsFormOptions
) => {
  return useFormik<FormikDisplaySettingsFields>({
    initialValues: {
      main: {
        adminPin: data?.adminPin ? data.adminPin.toString() : "",
        backgroundImageUrl: data?.backgroundImageUrl || "",
        logoImageUrl: data?.logoImageUrl || "",
        layoutTheme: data?.layoutTheme || DefaultThemeLayout,
      },
      display: {
        customerId: data?.customerId || "",
        showOrganizer: data?.showOrganizer || false,
        showMeetingTitle: data?.showMeetingTitle || false,
        showAttendees: data?.showAttendees || false,
        showRoomCapacity: data?.showRoomCapacity || false,
        showRoomAmeneties: data?.showRoomAmeneties || false,
        showCheckinRequired: !!data?.showCheckinRequired,
        checkinReminder: data?.checkinReminder || false,
        removeGhostRecurringEvents: data?.removeGhostRecurringEvents || false,
        excludeAllDayEventsFromCheckIn:
          data?.excludeAllDayEventsFromCheckIn ?? true,
        soonFree: data?.soonFree || false,
        energySaveMode: data?.energySaveMode || false,
        energySaveModeStart: data?.energySaveModeStart || "",
        energySaveModeEnd: data?.workingHrsStart || "",
        qrCodeRequired: data?.qrCodeRequired || false,
        textScaling: data?.textScaling || "normal",
        contentBoardActivated: data?.contentBoardActivated || false,
        contentBoardURL: data?.contentBoardURL || "",
        enableScreenLock: data?.enableScreenLock || false,
        screenLock: data?.screenLock || "",
        customLanguage: data?.customLanguage || undefined,
        bwFilter: data?.bwFilter || false,
        workingWeekDays: data?.workingWeekDays || [1, 2, 3, 4, 5],
        pendingTimeBeforeMeeting:
          data?.pendingTimeBeforeMeeting !== undefined
            ? data.pendingTimeBeforeMeeting
            : 15,
        pendingTimeAfterMeeting:
          data?.pendingTimeAfterMeeting !== undefined
            ? data.pendingTimeAfterMeeting
            : 15,
      },
      reservation: {
        sensorEntitlement: toSensorEntitlement(data?.sensorAutomationEnabled),
        motionSensorAutomation:
          toSensorEntitlement(data?.sensorAutomationEnabled) ===
          SensorEntitlement.ASSIGNED
            ? {
                preset: data?.preset || "custom",
                motionTriggerSensitivity:
                  data?.motionTriggerSensitivity || "responsive",
                autoReleaseDelay: data?.autoReleaseDelay || "cautious",
                vacancyCoolDown:
                  data?.vacancyCoolDown !== undefined
                    ? data.vacancyCoolDown
                    : 5,
                autoBookingDuration:
                  data?.autoBookingDuration !== undefined
                    ? data.autoBookingDuration
                    : 30,
              }
            : null,
        onScreenReservation: data?.onScreenReservation
          ? {
              requireMeetingTitleInput: data?.requireMeetingTitleInput,
              enforceWorkingHours: data?.enforceWorkingHours,
              showYourNameInput: data?.showYourNameInput,
              requireYourNameInput: data?.requireYourNameInput,
              showMeetingTitleInput: data?.showMeetingTitleInput,
              workingHrsActivated: data?.workingHrsActivated,
              requireHeadcount: data?.requireHeadcount,
              oneTimePinProtection: data?.oneTimePinProtection,
              maxDuration: data?.maxDuration ?? MaxDuration.REST_OF_DAY,
              workingHrsStart: data?.workingHrsStart,
              workingHrsEnd: data?.workingHrsEnd,
            }
          : null,
        deleteMeeting: data?.deleteMeeting || false,
        endMeetingEarly: data?.endMeetingEarly || false,
        extendMeeting: data?.extendMeeting || false,
        nearbyEnabled: data?.nearbyEnabled || false,
        nearbyRooms: data?.nearbyRooms || [],
        mapView: data?.mapView || false,
        floor: data?.floor || null,
      },
    },
    validateOnBlur: true,
    validateOnChange: true,
    enableReinitialize: true,
    validationSchema: DisplaySchema,
    ...opts,
    onSubmit: async (values, formikHelpers) => {
      await opts.onSubmit(values, formikHelpers);
    },
  });
};
